/**
 * _ReportShell — shared page chrome for all reports.
 * Header band, title, PDF + CSV + XLSX export buttons, and table styling tokens.
 *
 * W17-ReportsCatalog-Hub: added PDF/CSV/XLSX exports.
 * Export approach: PDF via window.print(); CSV client-side blob; XLSX via xlsx library.
 */
import { ReactNode } from "react";
import OperatorShell from "@/components/operator/OperatorShell";

export const BLACK  = "#0B0B0F";
export const RED    = "#DC2626";
export const SLATE  = "#6B6B70";
export const BG     = "#FAFAF7";
export const BORDER = "#E5E5E7";

export interface ReportShellProps {
  title: string;
  subtitle?: string;
  rightLabel?: string;
  /** @deprecated Use onExportCsv instead */
  onExport?: () => void;
  onExportCsv?: () => void;
  onExportXlsx?: () => void;
  /** If false, hides all export buttons. Default true. */
  exportsEnabled?: boolean;
  children: ReactNode;
}

// ── Export helpers ─────────────────────────────────────────────────────────

export function triggerPdfPrint(reportTitle: string) {
  const dateStr = new Date().toLocaleDateString("en-CA");
  const original = document.title;
  document.title = `Mi6 Group · Tenant 1 · ${reportTitle} · ${dateStr}`;
  window.print();
  document.title = original;
}

export function triggerCsvDownload(rows: Record<string, unknown>[], slug: string) {
  if (!rows || rows.length === 0) return;
  const dateStr = new Date().toLocaleDateString("en-CA");
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug}-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function triggerXlsxDownload(
  rows: Record<string, unknown>[],
  slug: string,
  sheetName = "Report"
) {
  import("xlsx")
    .then((XLSX) => {
      const dateStr = new Date().toLocaleDateString("en-CA");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${slug}-${dateStr}.xlsx`);
    })
    .catch(() => {
      alert("Excel export unavailable — use CSV instead.");
    });
}

// ── ReportShell component ──────────────────────────────────────────────────

export default function ReportShell(props: ReportShellProps) {
  const {
    title,
    subtitle,
    onExport,
    onExportCsv,
    onExportXlsx,
    exportsEnabled = true,
    children,
  } = props;

  const csvHandler = onExportCsv ?? onExport;

  function handlePdf() {
    triggerPdfPrint(title);
  }

  return (
    <>
      <style>{`
        @media print {
          nav, aside, [data-sidebar], [data-topbar], .no-print { display: none !important; }
          body { background: #fff; }
        }
      `}</style>

      <OperatorShell breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: title }]}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 24,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>
                {title}
              </h1>
              {subtitle && (
                <div style={{ fontSize: 13, color: SLATE, marginTop: 6 }}>{subtitle}</div>
              )}
            </div>

            {exportsEnabled && (
              <div
                className="no-print"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <ExportBtn label="PDF" onClick={handlePdf} />
                {onExportXlsx && <ExportBtn label="Excel" onClick={onExportXlsx} />}
                {csvHandler && <ExportBtn label="CSV" onClick={csvHandler} />}
              </div>
            )}
          </div>

          {children}
        </div>
      </OperatorShell>
    </>
  );
}

function ExportBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 14px",
        borderRadius: 6,
        background: "#FFFFFF",
        color: BLACK,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        border: `1px solid ${BLACK}`,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

// ── Table styling tokens ────────────────────────────────────────────────────

export const thStyle: React.CSSProperties = {
  textAlign: "left",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: SLATE,
  padding: "10px 12px",
  borderBottom: `1px solid ${BORDER}`,
  background: "#FFFFFF",
  position: "sticky",
  top: 0,
};

export const tdStyle: React.CSSProperties = {
  fontSize: 13,
  color: BLACK,
  padding: "10px 12px",
  borderBottom: `1px solid ${BORDER}`,
  background: "#FFFFFF",
  verticalAlign: "top",
};

export function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        background: "#FFFFFF",
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 14, color: SLATE }}>{message}</div>
    </div>
  );
}

export function LoadingState() {
  return (
    <div
      style={{
        padding: 48,
        textAlign: "center",
        background: "#FFFFFF",
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 13, color: SLATE, fontStyle: "italic" }}>Loading...</div>
    </div>
  );
}
